import { Dispatch, SetStateAction, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { v7 as uuidv7 } from "uuid";

import {
  CompaniesHierarchyDataDto,
  inputFieldMarginBottom
} from "../../../../../../shared";
import useCompaniesFormInputs from "../../../../../../shared/hooks/useCompaniesFormInputs/useCompaniesFormInputs";
import InputField from "../../../../../InputField/InputField";
import { EditCompanyPayloadType } from "../../../../types";

interface IEditCompanyDialogContentProps {
  rowData: CompaniesHierarchyDataDto;
  setPayload: Dispatch<SetStateAction<EditCompanyPayloadType>>;
  setIsReadyToConfirm: Dispatch<SetStateAction<boolean | "" | null>>;
  isLoading: boolean;
}

const EditCompanyDialogContent = ({
  rowData,
  setPayload,
  setIsReadyToConfirm,
  isLoading
}: IEditCompanyDialogContentProps) => {
  const { t } = useTranslation();

  const [name, setName] = useState<string>(rowData.name);
  const [description, setDescription] = useState<string>(rowData.comment ?? "");
  const [nameInputError, setNameInputError] = useState<string | null>(null);
  const [descriptionInputError, setDescriptionInputError] = useState<
    string | null
  >(null);

  const {
    validateNameLength,
    handleNameChangeValue,
    validateDescriptionLength,
    handleChangeDescription,
    isNameValid,
    isDescriptionValid
  } = useCompaniesFormInputs({
    setNameInputError,
    setName,
    setDescription,
    setDescriptionInputError
  });

  const isPayloadReadyToConfirm =
    isNameValid(name) && isDescriptionValid(description);

  useEffect(() => {
    setPayload({
      id: rowData.id,
      name: name.trim(),
      comment: description.trim()
    });
    setIsReadyToConfirm(isPayloadReadyToConfirm);
  }, [
    description,
    isPayloadReadyToConfirm,
    name,
    rowData,
    setIsReadyToConfirm,
    setPayload
  ]);

  return (
    <>
      {rowData.parentCompanyId !== null &&
        rowData.parentCompanyName !== null && (
          <InputField
            id={uuidv7()}
            size="medium"
            data-testid="parent-company-name"
            fullWidth
            readOnly
            labelLeft={t("Dialog##parent company")}
            placeholder={rowData.parentCompanyName}
            customStyle={inputFieldMarginBottom}
          />
        )}

      <InputField
        id={uuidv7()}
        testId="company-name-input-field"
        value={name}
        size="medium"
        fullWidth
        required
        errorText={isNameValid(name) ? "" : nameInputError}
        onBlur={validateNameLength}
        onChange={handleNameChangeValue}
        labelLeft={t("Dialog##company name")}
        placeholder={t("Dialog##company name placeholder")}
        customStyle={inputFieldMarginBottom}
        disabled={isLoading}
      />
      <InputField
        id={uuidv7()}
        testId="company-description-input-field"
        value={description}
        size="medium"
        fullWidth
        errorText={isDescriptionValid(description) ? "" : descriptionInputError}
        onBlur={validateDescriptionLength}
        onChange={handleChangeDescription}
        labelLeft={t("Dialog##description")}
        placeholder={t("Dialog##enter here")}
        customStyle={inputFieldMarginBottom}
        disabled={isLoading}
      />
    </>
  );
};

export default EditCompanyDialogContent;
